<div class="order-panel">
    @if (!booking.order && !booking.docket) {
        <header class="order-panel__intro">
            <h5>Start an order</h5>
            <p>Collect menu choices in advance, or record items for the booking.</p>
        </header>
        <section class="order-panel__card" aria-labelledby="preorder-start-title">
            <div class="order-panel__start">
                <span class="order-panel__icon" aria-hidden="true"><img ngSrc="assets/food-drink/normal.svg" alt="" width="24" height="24" /></span>
                <div>
                    <h6 id="preorder-start-title">Menu pre-order</h6>
                    <p>Send a menu for guests to choose from. Track replies and edit their choices here.</p>
                </div>
            </div>
            <button type="button" class="btn btn-primary align-self-start" data-testid="choose-menu-button" (click)="onAddMenu()">Choose menu</button>
        </section>
        <section class="order-panel__card" aria-labelledby="upsell-start-title">
            <div class="order-panel__start">
                <span class="order-panel__icon order-panel__icon--neutral" aria-hidden="true"><img ngSrc="assets/plus/normal.svg" alt="" width="24" height="24" /></span>
                <div>
                    <h6 id="upsell-start-title">Upsell order</h6>
                    <p>Add products and quantities yourself, such as drinks or extras for the table.</p>
                </div>
            </div>
            <button type="button" class="btn btn-outline-secondary align-self-start" data-testid="add-upsell-order-button" (click)="onAddOrder()">Add upsell order</button>
        </section>
    }

    @if (booking.docket) {
        <section class="order-panel__card" aria-labelledby="preorder-title" data-testid="preorder-overview">
            <div class="order-panel__heading">
                <div class="order-panel__title">
                    <div class="order-panel__eyebrow">Menu pre-order</div>
                    <h5 id="preorder-title">{{ menu?.displayName || (loadingMenu ? 'Loading menu…' : 'Menu unavailable') }}</h5>
                </div>
                <div class="vstack gap-2 d-none d-md-flex flex-grow-0 flex-shrink-0">
                    <button type="button" class="btn btn-outline-secondary" (click)="onPrintOrder()" [disabled]="!menu" data-testid="print-preorder-button">Print order</button>
                    <button type="button" class="btn btn-outline-danger" (click)="onRemoveDocket()" data-testid="remove-preorder-button">Remove pre-order</button>
                </div>
                <div class="d-md-none" ngbDropdown placement="bottom-end">
                    <button type="button" class="btn btn-outline-secondary" ngbDropdownToggle data-testid="preorder-actions-button">More</button>
                    <div ngbDropdownMenu>
                        <button type="button" ngbDropdownItem (click)="onPrintOrder()" [disabled]="!menu" data-testid="print-preorder-menu-button">Print order</button>
                        <div class="dropdown-divider"></div>
                        <button type="button" ngbDropdownItem class="text-danger" (click)="onRemoveDocket()" data-testid="remove-preorder-menu-button">Remove pre-order</button>
                    </div>
                </div>
            </div>
            @if (menu) {
                <div class="order-panel__menu-settings">
                    <p>{{ menu.linkType === MenuLinkType.PerCover ? 'Each guest submits their own choices.' : 'One organiser enters everyone’s choices.' }}</p>
                </div>
            } @else if (!loadingMenu) {
                <p role="status">Menu details could not be loaded. Reopen this booking to try again.</p>
            }

            <div class="order-panel__progress">
                <div class="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                    <span><strong>{{ docketCompletedCount }} / {{ booking.size }}</strong> guest choices entered</span>
                    <span class="order-panel__status" [class.order-panel__status--complete]="docketIsComplete" data-testid="preorder-status">
                        {{ docketIsComplete ? 'All choices entered' : docketCompletedCount === 0 ? 'Awaiting choices' : (booking.size - docketCompletedCount) + ' still to reply' }}
                    </span>
                </div>
                <div class="progress" role="progressbar" aria-label="Choices progress" [attr.aria-valuenow]="docketCompletedCount" aria-valuemin="0" [attr.aria-valuemax]="booking.size">
                    <div class="progress-bar" [style.width.%]="docketProgressPercent"></div>
                </div>
            </div>

            <div class="order-panel__actions">
                <ng-container *ngTemplateOutlet="orderLinkActions; context: { payment: false }"></ng-container>
                @if (hasPersonSelections) {
                    <button type="button" class="btn" [class.btn-primary]="docketIsComplete" [class.btn-outline-secondary]="!docketIsComplete" data-testid="order-details-button" (click)="onOpenOrderDetails()">View order</button>
                }
                <button type="button" class="btn btn-outline-secondary" data-testid="edit-choices-button" (click)="onEditDocket('choices')" [disabled]="!menu">Edit choices</button>
                <button type="button" class="btn btn-outline-secondary" data-testid="edit-course-limits-button" (click)="onEditDocket('limits')" [disabled]="!menu">Edit course limits</button>
            </div>
        </section>

        @if (selectionsByPerson.length > 0) {
            <section class="order-panel__guests" aria-labelledby="guest-choices-title">
                <h6 id="guest-choices-title">Guest choices</h6>
                <div class="order-panel__guest-list">
                    @for (person of selectionsByPerson; track person.index) {
                        <div class="order-panel__guest" [attr.data-testid]="'docket-choice-' + person.index">
                            <div class="order-panel__guest-heading">
                                <span class="order-panel__guest-number" aria-hidden="true">{{ person.index + 1 }}</span>
                                <h6>{{ person.title }}</h6>
                                @if (person.items.length === 0) { <span class="small text-body-secondary ms-auto">Awaiting choices</span> }
                            </div>
                            @if (person.items.length > 0) {
                                <ul class="order-panel__items">
                                    @for (item of person.items; track $index) { <li>{{ item }}</li> }
                                </ul>
                            }
                            @if (person.notes) {
                                <div class="order-panel__guest-note"><span class="fw-semibold">Note:</span> {{ person.notes }}</div>
                            }
                        </div>
                    }
                </div>
            </section>
        } @else if (menu) {
            <div class="order-panel__empty" data-testid="preorder-empty-state">
                <h6>No guest choices yet</h6>
                <p>Share the menu link to collect replies, or use “Edit choices” to enter them yourself.</p>
            </div>
        }
    }

    @if (booking.order && !booking.docket) {
        <section class="order-panel__card" aria-labelledby="upsell-title" data-testid="upsell-overview">
            <div class="order-panel__heading">
                <div class="order-panel__title">
                    <div class="order-panel__eyebrow">Products &amp; quantities</div>
                    <h5 id="upsell-title">Upsell order</h5>
                </div>
                <div class="vstack gap-2 d-none d-md-flex flex-grow-0 flex-shrink-0">
                    <button type="button" class="btn btn-outline-secondary" (click)="onPrintOrder()" data-testid="print-upsell-button">Print order</button>
                    @if (!booking.order.isPaid) {
                        <button type="button" class="btn btn-outline-danger" (click)="onRemoveOrder()" data-testid="remove-upsell-button">Remove upsell order</button>
                    }
                </div>
                <div class="d-md-none" ngbDropdown placement="bottom-end">
                    <button type="button" class="btn btn-outline-secondary" ngbDropdownToggle data-testid="upsell-actions-button">More</button>
                    <div ngbDropdownMenu>
                        <button type="button" ngbDropdownItem (click)="onPrintOrder()">Print order</button>
                        @if (!booking.order.isPaid) {
                            <div class="dropdown-divider"></div>
                            <button type="button" ngbDropdownItem class="text-danger" (click)="onRemoveOrder()">Remove upsell order</button>
                        }
                    </div>
                </div>
            </div>
            <p>Items requested for this booking.</p>
            <div class="order-panel__line-items">
                @for (item of booking.order.lineItems; track item.id) {
                    <div class="order-panel__line-item">
                        <span class="order-panel__quantity">{{ item.quantity }} ×</span>
                        <div>
                            <span class="fw-semibold">{{ item.name }}</span>
                            @if (booking.order.isPaid && item.paidOnline) {
                                <div class="small">{{ item.unitAmount() / 100 | currency:booking.order.currencyCode }} each · {{ item.unitAmount() * item.quantity / 100 | currency:booking.order.currencyCode }}</div>
                            }
                            @for (modifier of item.modifiers; track modifier.id) {
                                <div class="small text-body-secondary">{{ modifier.name }} × {{ modifier.quantity }}</div>
                            }
                        </div>
                    </div>
                } @empty { <p>No items in this order.</p> }
            </div>
            @if (booking.order.isPaid) {
                <p class="fw-semibold">Total: {{ booking.order.total() / 100 | currency:booking.order.currencyCode }}</p>
                <p class="small text-body-secondary">Products and quantities are locked for this paid order. Refunds are available in the payment section.</p>
            }
            @if (booking.order.message) { <p><span class="fw-semibold">Order note:</span> {{ booking.order.message }}</p> }
            <div class="order-panel__actions">
                @if (booking.canPayForOrder()) {
                    <ng-container *ngTemplateOutlet="orderLinkActions; context: { payment: true }"></ng-container>
                }
                <button type="button" class="btn" [class.btn-primary]="!booking.canPayForOrder()" [class.btn-outline-secondary]="booking.canPayForOrder()" data-testid="edit-upsell-order-button" (click)="onEditOrder()">{{ booking.order.isPaid ? 'Edit order note' : 'Edit order' }}</button>
            </div>
        </section>
    }
</div>

<ng-template #orderLinkActions let-payment="payment">
    <div class="btn-group" ngbDropdown [placement]="payment ? 'bottom-end' : 'bottom-start'">
        <button type="button" class="btn" [class.btn-primary]="payment || !docketIsComplete" [class.btn-outline-secondary]="!payment && docketIsComplete" [attr.data-testid]="payment ? 'resend-order-payment-link' : 'resend-menu-link-button'" (click)="onResendOrderLink()" [disabled]="(!payment && !menu) || (!booking.emailAddress && !booking.phoneNumber)">{{ payment ? 'Send payment link' : 'Send menu link' }}</button>
        <button type="button" class="btn dropdown-toggle-split" [class.btn-primary]="payment || !docketIsComplete" [class.btn-outline-secondary]="!payment && docketIsComplete" ngbDropdownToggle [attr.aria-label]="payment ? 'Payment link options' : 'Menu link options'" [attr.data-testid]="payment ? 'order-payment-link-options' : 'menu-link-options-button'" [disabled]="!payment && !menu"></button>
        <div ngbDropdownMenu [class.order-panel__link-menu]="!payment">
            @if (payment) {
                <button type="button" ngbDropdownItem (click)="copyPaymentLinkClicked.emit()" data-testid="copy-order-payment-link">Copy payment link</button>
            } @else {
                <button type="button" ngbDropdownItem (click)="onCopyMenuLink(MenuLinkType.PerCover)" data-testid="copy-guest-menu-link-button">
                    <span class="fw-semibold">Copy individual guest link</span>
                    <span class="order-panel__menu-help">Each guest submits their own choices</span>
                    @if (menu?.linkType !== MenuLinkType.PerCover) {
                        <span class="alert alert-warning d-block small text-wrap mt-2 mb-0 p-2" data-testid="menu-link-type-warning">This menu does not normally use an individual guest link. Copy this link only if you want guests to enter their own choices.</span>
                    }
                </button>
                <button type="button" ngbDropdownItem (click)="onCopyMenuLink(MenuLinkType.SingleOrganiser)" data-testid="copy-organiser-menu-link-button">
                    <span class="fw-semibold">Copy organiser link</span>
                    <span class="order-panel__menu-help">One person enters choices for the group</span>
                    @if (menu?.linkType !== MenuLinkType.SingleOrganiser) {
                        <span class="alert alert-warning d-block small text-wrap mt-2 mb-0 p-2" data-testid="menu-link-type-warning">This menu does not normally use an organiser link. Copy this link only if you want one person to enter choices for the group.</span>
                    }
                </button>
            }
        </div>
    </div>
</ng-template>

<ng-template #printContent>
    <app-print-single-booking-order
        [booking]="booking"
        [venue]="venue"
        [productLibrary]="productLibrary"
    >
    </app-print-single-booking-order>
</ng-template>

<iframe
    #printIframe
    width="0"
    height="0"
>
</iframe>
